import { useState, useEffect, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { AlertCircle, Loader, ChevronLeft, ChevronRight, HelpCircle, Lock } from "react-feather";
import {
  taxProfileService,
  AffiliateTaxProfileFormType,
  AffiliateTaxProfileEntityType,
  CreateTaxProfilePayload,
  TaxProfile,
} from "../../services/tax-profile.service";
import { toast } from "react-hot-toast";
import countriesData from "../../assets/countries.json";
import { Button } from "../ui/button";

interface Country {
  code: string;
  name: string;
  currency: {
    currencyName: string;
    currencyCode: string;
    currencySymbol: string;
  };
  geo: {
    latitude: number;
    longitude: number;
  };
  capital: string;
  phone: string;
}

interface TaxInformationModalProps {
  isOpen: boolean;
  taxYear: number;
  onComplete: () => void;
}

type Step = 'residency' | 'w9-identity' | 'w9-taxid' | 'w9-address' | 'w9-certification' | 'w8ben-identity' | 'w8ben-address' | 'w8ben-taxid' | 'w8ben-certification';

export function TaxInformationModal({
  isOpen,
  taxYear,
  onComplete,
}: TaxInformationModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingProfile, setExistingProfile] = useState<TaxProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUSPerson, setIsUSPerson] = useState<boolean | null>(null);
  const [currentStep, setCurrentStep] = useState<Step>('residency');
  const [certificationChecked, setCertificationChecked] = useState(false);
  const [signature, setSignature] = useState("");
  
  // Format country name from uppercase to Title Case
  const formatCountryName = (name: string): string => {
    return name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get sorted countries list (excluding US for W-8BEN forms)
  const countries = useMemo(() => {
    return (countriesData as Country[])
      .filter(country => country.code !== 'US')
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(country => ({
        code: country.code,
        name: formatCountryName(country.name),
      }));
  }, []);
  
  const [formData, setFormData] = useState<CreateTaxProfilePayload>({
    taxYear,
    formType: AffiliateTaxProfileFormType.W9,
    legalName: "",
    businessName: "",
    entityType: AffiliateTaxProfileEntityType.INDIVIDUAL,
    taxCountry: "US",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "US",
    },
    taxId: "",
  });

  // W-8BEN specific fields
  const [w8benData, setW8benData] = useState({
    countryOfCitizenship: "",
    countryOfTaxResidence: "",
    permanentAddress: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    mailingAddress: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    foreignTaxId: "",
  });

  useEffect(() => {
    if (isOpen) {
      setIsUSPerson(null);
      setCurrentStep('residency');
      setCertificationChecked(false);
      setSignature("");
      
      // Check if profile already exists for this year
      setIsLoading(true);
      taxProfileService
        .findByYear(taxYear)
        .then((profile) => {
          if (profile) {
            setExistingProfile(profile);
            setIsUSPerson(profile.formType === AffiliateTaxProfileFormType.W9);
            setFormData({
              taxYear: profile.taxYear,
              formType: profile.formType,
              legalName: profile.legalName,
              businessName: profile.businessName || "",
              entityType: profile.entityType,
              taxCountry: profile.taxCountry,
              address: profile.address,
              taxId: profile.taxId,
            });
            // Set initial step based on form type
            if (profile.formType === AffiliateTaxProfileFormType.W9) {
              setCurrentStep('w9-identity');
            } else {
              setCurrentStep('w8ben-identity');
            }
          }
        })
        .catch(() => {
          // No existing profile, continue with empty form
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, taxYear]);

  const handleResidencySelection = (isUS: boolean) => {
    setIsUSPerson(isUS);
    if (isUS) {
      setFormData(prev => ({ ...prev, formType: AffiliateTaxProfileFormType.W9, taxCountry: "US" }));
      setCurrentStep('w9-identity');
    } else {
      setFormData(prev => ({ ...prev, formType: AffiliateTaxProfileFormType.W8BEN }));
      setCurrentStep('w8ben-identity');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));
  };

  const handleW8benChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name.startsWith('permanent.')) {
      const field = name.replace('permanent.', '');
      setW8benData(prev => ({
        ...prev,
        permanentAddress: {
          ...prev.permanentAddress,
          [field]: value,
        },
      }));
    } else if (name.startsWith('mailing.')) {
      const field = name.replace('mailing.', '');
      setW8benData(prev => ({
        ...prev,
        mailingAddress: {
          ...prev.mailingAddress,
          [field]: value,
        },
      }));
    } else {
      setW8benData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Format SSN/EIN: XXX-XX-XXXX
  const formatTaxId = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  };

  const handleTaxIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatTaxId(e.target.value);
    setFormData(prev => ({ ...prev, taxId: formatted }));
  };

  const getW9Steps = (): Step[] => ['w9-identity', 'w9-taxid', 'w9-address', 'w9-certification'];
  const getW8benSteps = (): Step[] => ['w8ben-identity', 'w8ben-address', 'w8ben-taxid', 'w8ben-certification'];

  const getCurrentStepIndex = (): number => {
    if (isUSPerson) {
      return getW9Steps().indexOf(currentStep);
    } else if (isUSPerson === false) {
      return getW8benSteps().indexOf(currentStep);
    }
    return -1;
  };

  const getTotalSteps = (): number => {
    return isUSPerson ? 4 : 4;
  };

  const handleNext = () => {
    if (isUSPerson) {
      const steps = getW9Steps();
      const currentIndex = steps.indexOf(currentStep);
      if (currentIndex < steps.length - 1) {
        setCurrentStep(steps[currentIndex + 1]);
      }
    } else if (isUSPerson === false) {
      const steps = getW8benSteps();
      const currentIndex = steps.indexOf(currentStep);
      if (currentIndex < steps.length - 1) {
        setCurrentStep(steps[currentIndex + 1]);
      }
    }
  };

  const handleBack = () => {
    if (isUSPerson) {
      const steps = getW9Steps();
      const currentIndex = steps.indexOf(currentStep);
      if (currentIndex > 0) {
        setCurrentStep(steps[currentIndex - 1]);
      } else {
        setCurrentStep('residency');
        setIsUSPerson(null);
      }
    } else if (isUSPerson === false) {
      const steps = getW8benSteps();
      const currentIndex = steps.indexOf(currentStep);
      if (currentIndex > 0) {
        setCurrentStep(steps[currentIndex - 1]);
      } else {
        setCurrentStep('residency');
        setIsUSPerson(null);
      }
    }
  };

  const validateCurrentStep = (): boolean => {
    if (currentStep === 'w9-identity') {
    if (!formData.legalName.trim()) {
      toast.error("Legal name is required");
      return false;
    }
      return true;
    }
    
    if (currentStep === 'w9-taxid') {
      const taxIdDigits = formData.taxId?.replace(/\D/g, "") || "";
      if (taxIdDigits.length !== 9) {
        toast.error("Tax ID must be 9 digits (SSN or EIN)");
      return false;
      }
      return true;
    }
    
    if (currentStep === 'w9-address') {
      if (!formData.address.street?.trim()) {
        toast.error("Street address is required");
        return false;
      }
      if (!formData.address.city?.trim()) {
        toast.error("City is required");
        return false;
      }
      if (!formData.address.state?.trim()) {
        toast.error("State is required");
        return false;
      }
      if (!formData.address.zipCode?.trim()) {
        toast.error("ZIP code is required");
        return false;
      }
      return true;
    }
    
    if (currentStep === 'w9-certification') {
      if (!certificationChecked) {
        toast.error("You must certify the information is correct");
        return false;
      }
      if (!signature.trim()) {
        toast.error("Signature is required");
        return false;
      }
      return true;
    }
    
    if (currentStep === 'w8ben-identity') {
      if (!formData.legalName.trim()) {
        toast.error("Full legal name is required");
        return false;
      }
      if (!w8benData.countryOfCitizenship) {
        toast.error("Country of citizenship is required");
        return false;
      }
      if (!w8benData.countryOfTaxResidence) {
        toast.error("Country of tax residence is required");
      return false;
      }
      return true;
    }
    
    if (currentStep === 'w8ben-address') {
      if (!w8benData.permanentAddress.street.trim()) {
        toast.error("Permanent address street is required");
        return false;
      }
      if (!w8benData.permanentAddress.city.trim()) {
        toast.error("Permanent address city is required");
        return false;
      }
      if (!w8benData.permanentAddress.country) {
        toast.error("Permanent address country is required");
      return false;
      }
      return true;
    }
    
    if (currentStep === 'w8ben-certification') {
      if (!certificationChecked) {
        toast.error("You must certify the information is correct");
        return false;
      }
      if (!signature.trim()) {
        toast.error("Signature is required");
        return false;
      }
      return true;
    }
    
    return true;
  };

  const handleStepNext = () => {
    if (validateCurrentStep()) {
      // If on last step, submit
      const stepIndex = getCurrentStepIndex();
      const totalSteps = getTotalSteps();
      if (stepIndex === totalSteps - 1) {
        handleSubmit();
      } else {
        handleNext();
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      return;
    }

    setIsSubmitting(true);
    try {
      let payload: CreateTaxProfilePayload;
      
      if (isUSPerson) {
        // W-9 payload
        payload = {
          taxYear: formData.taxYear,
          formType: AffiliateTaxProfileFormType.W9,
          legalName: formData.legalName,
          businessName: formData.businessName || undefined,
          entityType: formData.entityType,
          taxCountry: "US",
          address: formData.address,
          taxId: formData.taxId,
          signature,
        };
      } else {
        // W-8BEN payload
        const w8benAddress: any = {
          permanent: w8benData.permanentAddress,
          countryOfCitizenship: w8benData.countryOfCitizenship,
          countryOfTaxResidence: w8benData.countryOfTaxResidence,
        };
        if (w8benData.mailingAddress.street) {
          w8benAddress.mailing = w8benData.mailingAddress;
        }
        
        payload = {
          taxYear: formData.taxYear,
          formType: AffiliateTaxProfileFormType.W8BEN,
          legalName: formData.legalName,
          entityType: AffiliateTaxProfileEntityType.INDIVIDUAL,
          taxCountry: w8benData.countryOfTaxResidence,
          address: w8benAddress,
          taxId: w8benData.foreignTaxId || undefined,
          signature,
        };
      }

      if (existingProfile) {
        await taxProfileService.update(existingProfile.id, payload);
        toast.success("Tax information updated successfully");
      } else {
        await taxProfileService.create(payload);
        toast.success("Tax information submitted successfully");
      }
      onComplete();
    } catch (error: any) {
      console.error("Error saving tax information:", error);
      toast.error(
        error?.response?.data?.message ||
          "Failed to save tax information. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderResidencyScreen = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Tax Residency
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Are you a U.S. person for tax purposes?
        </p>
        <div className="flex items-start gap-2 text-xs text-muted-foreground mb-6">
          <HelpCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>This is based on citizenship or tax residency, not where you live. This determines W-9 vs W-8BEN.</span>
        </div>
      </div>
      
      <div className="space-y-3">
        <button
          onClick={() => handleResidencySelection(true)}
          className="w-full px-6 py-4 border-2 border-border rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-colors text-left"
        >
          <div className="font-medium text-foreground">Yes, I am a U.S. person</div>
        </button>
        <button
          onClick={() => handleResidencySelection(false)}
          className="w-full px-6 py-4 border-2 border-border rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-colors text-left"
        >
          <div className="font-medium text-foreground">No, I am not a U.S. person</div>
        </button>
      </div>
    </div>
  );

  const renderW9Identity = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Step 1: Identity</h3>
      
      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          Legal name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="legalName"
          value={formData.legalName}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
          placeholder="Enter your legal name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          Business name (optional)
        </label>
        <input
          type="text"
          name="businessName"
          value={formData.businessName}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
          placeholder="Enter business name if applicable"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          Entity type <span className="text-red-500">*</span>
        </label>
        <select
          name="entityType"
          value={formData.entityType}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
        >
          <option value={AffiliateTaxProfileEntityType.INDIVIDUAL}>Individual</option>
          <option value={AffiliateTaxProfileEntityType.SOLE_PROPRIETOR}>Sole Proprietor</option>
          <option value={AffiliateTaxProfileEntityType.SINGLE_MEMBER_LLC}>Single Member LLC</option>
          <option value={AffiliateTaxProfileEntityType.MULTI_MEMBER_LLC}>Multi Member LLC</option>
          <option value={AffiliateTaxProfileEntityType.C_CORP}>C Corporation</option>
          <option value={AffiliateTaxProfileEntityType.S_CORP}>S Corporation</option>
          <option value={AffiliateTaxProfileEntityType.PARTNERSHIP}>Partnership</option>
          <option value={AffiliateTaxProfileEntityType.TRUST}>Trust</option>
        </select>
      </div>
    </div>
  );

  const renderW9TaxId = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Step 2: Tax ID</h3>
      
      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          SSN or EIN <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="taxId"
          value={formData.taxId}
          onChange={handleTaxIdChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
          placeholder="XXX-XX-XXXX"
          maxLength={11}
        />
        <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
          <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>Your information is encrypted and never shared via email.</span>
        </div>
      </div>
    </div>
  );

  const renderW9Address = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Step 3: Address</h3>
      
      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          Street <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="street"
          value={formData.address.street}
          onChange={handleAddressChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
          placeholder="Enter street address"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="city"
            value={formData.address.city}
            onChange={handleAddressChange}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
            disabled={isSubmitting}
            placeholder="City"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            State <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="state"
            value={formData.address.state}
            onChange={handleAddressChange}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
            disabled={isSubmitting}
            placeholder="State"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          ZIP <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="zipCode"
          value={formData.address.zipCode}
          onChange={handleAddressChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
          placeholder="ZIP code"
        />
      </div>
    </div>
  );

  const renderW9Certification = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Step 4: Certification</h3>
      
      <div className="space-y-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={certificationChecked}
            onChange={(e) => setCertificationChecked(e.target.checked)}
            className="mt-1 h-4 w-4 text-brand-600 focus:ring-brand-500 border-border rounded"
            disabled={isSubmitting}
          />
          <span className="text-sm text-muted-foreground">
            I certify that the information provided is correct and I am not subject to backup withholding.
          </span>
        </label>

        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Signature (Type your full name) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
            disabled={isSubmitting}
            placeholder="Type your full name"
          />
        </div>
      </div>
    </div>
  );

  const renderW8benIdentity = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Step 1: Identity</h3>
      
      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          Full legal name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="legalName"
          value={formData.legalName}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
          placeholder="Enter your full legal name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          Country of citizenship <span className="text-red-500">*</span>
        </label>
        <select
          name="countryOfCitizenship"
          value={w8benData.countryOfCitizenship}
          onChange={handleW8benChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
        >
          <option value="">Select country</option>
          {countries.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          Country of tax residence <span className="text-red-500">*</span>
        </label>
        <select
          name="countryOfTaxResidence"
          value={w8benData.countryOfTaxResidence}
          onChange={handleW8benChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
        >
          <option value="">Select country</option>
          {countries.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderW8benAddress = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Step 2: Address</h3>
      
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Permanent address (non-US) <span className="text-red-500">*</span></h4>
          <div className="space-y-3">
            <input
              type="text"
              name="permanent.street"
              value={w8benData.permanentAddress.street}
              onChange={handleW8benChange}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Street address"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                name="permanent.city"
                value={w8benData.permanentAddress.city}
                onChange={handleW8benChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="City"
              />
              <input
                type="text"
                name="permanent.state"
                value={w8benData.permanentAddress.state}
                onChange={handleW8benChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="State/Province"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                name="permanent.zipCode"
                value={w8benData.permanentAddress.zipCode}
                onChange={handleW8benChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="ZIP/Postal code"
              />
              <select
                name="permanent.country"
                value={w8benData.permanentAddress.country}
                onChange={handleW8benChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">Select country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Mailing address (optional)</h4>
          <div className="space-y-3">
            <input
              type="text"
              name="mailing.street"
              value={w8benData.mailingAddress.street}
              onChange={handleW8benChange}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Street address"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                name="mailing.city"
                value={w8benData.mailingAddress.city}
                onChange={handleW8benChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="City"
              />
              <input
                type="text"
                name="mailing.state"
                value={w8benData.mailingAddress.state}
                onChange={handleW8benChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="State/Province"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                name="mailing.zipCode"
                value={w8benData.mailingAddress.zipCode}
                onChange={handleW8benChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="ZIP/Postal code"
              />
              <select
                name="mailing.country"
                value={w8benData.mailingAddress.country}
                onChange={handleW8benChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">Select country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderW8benTaxId = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Step 3: Foreign Tax ID (Optional)</h3>
      
      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1">
          Foreign tax identification number
        </label>
        <input
          type="text"
          name="foreignTaxId"
          value={w8benData.foreignTaxId}
          onChange={handleW8benChange}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          disabled={isSubmitting}
          placeholder="Enter foreign tax ID if applicable"
        />
      </div>
    </div>
  );

  const renderW8benCertification = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Step 4: Certification</h3>
      
      <div className="space-y-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={certificationChecked}
            onChange={(e) => setCertificationChecked(e.target.checked)}
            className="mt-1 h-4 w-4 text-brand-600 focus:ring-brand-500 border-border rounded"
            disabled={isSubmitting}
          />
          <span className="text-sm text-muted-foreground">
            I certify that I am not a U.S. person and the information provided is true.
          </span>
        </label>

        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Signature (Type your name) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
            disabled={isSubmitting}
            placeholder="Type your name"
          />
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-8">
          <Loader className="w-6 h-6 animate-spin text-brand-600" />
        </div>
      );
    }

    if (currentStep === 'residency') {
      return renderResidencyScreen();
    }

    if (isUSPerson) {
      switch (currentStep) {
        case 'w9-identity':
          return renderW9Identity();
        case 'w9-taxid':
          return renderW9TaxId();
        case 'w9-address':
          return renderW9Address();
        case 'w9-certification':
          return renderW9Certification();
        default:
          return null;
      }
    } else if (isUSPerson === false) {
      switch (currentStep) {
        case 'w8ben-identity':
          return renderW8benIdentity();
        case 'w8ben-address':
          return renderW8benAddress();
        case 'w8ben-taxid':
          return renderW8benTaxId();
        case 'w8ben-certification':
          return renderW8benCertification();
        default:
          return null;
      }
    }

    return null;
  };

  const stepIndex = getCurrentStepIndex();
  const totalSteps = getTotalSteps();
  const showProgress = isUSPerson !== null && currentStep !== 'residency';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-card p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-foreground mb-4 flex items-center gap-2"
                >
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  Tax Information Required
                </Dialog.Title>

                {currentStep !== 'residency' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-orange-800">
                      You must provide tax information before requesting a payout
                      because your total earnings for {taxYear} exceed $600. This
                      information is required by law for tax reporting purposes.
                    </p>
                  </div>
                )}

                {showProgress && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        Step {stepIndex + 1} of {totalSteps}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {Math.round(((stepIndex + 1) / totalSteps) * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-brand-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
                      />
                      </div>
                    </div>
                  )}

                <div className="mt-2">
                  {renderCurrentStep()}
                </div>

                <div className="mt-6 flex justify-between">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={isSubmitting || isLoading || currentStep === 'residency'}
                    className="inline-flex items-center justify-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </button>
                  
                  <Button
                    type="button"
                    onClick={handleStepNext}
                    disabled={isSubmitting || isLoading}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : stepIndex === totalSteps - 1 ? (
                      "Submit"
                    ) : (
                      <>
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
